/**
 * 経路の線（docs/05_rendering-and-audio.md 9 章）。
 * 線は描画している地面に沿わせて作り（terrainMesh.ts drapePolyline）、浮かせる高さはカメラの高さに合わせて
 * 毎フレーム object.position.y で変える（overlayLift）。
 */
import * as THREE from 'three';
import type { EdgeData } from '../../core/world';
import type { Arc } from '../../sim/world/graph';
import { LineMaterial } from 'three/addons/lines/LineMaterial.js';
import { LineSegments2 } from 'three/addons/lines/LineSegments2.js';
import { LineSegmentsGeometry } from 'three/addons/lines/LineSegmentsGeometry.js';

/** すべての経路の細い線。drape：折れ線を地面に沿わせる関数 */
export function createEdgeLines(
  edges: EdgeData[],
  drape: (pts: readonly number[]) => number[],
): THREE.LineSegments {
  const draped = edges.map((e) => drape(Array.from(e.polyline)));
  let count = 0;
  for (const p of draped) count += Math.max(0, p.length / 3 - 1);
  const pos = new Float32Array(count * 6);
  let o = 0;
  for (const p of draped) {
    for (let i = 0; i + 1 < p.length / 3; i++) {
      pos[o++] = p[i * 3] as number;
      pos[o++] = p[i * 3 + 1] as number;
      pos[o++] = p[i * 3 + 2] as number;
      pos[o++] = p[i * 3 + 3] as number;
      pos[o++] = p[i * 3 + 4] as number;
      pos[o++] = p[i * 3 + 5] as number;
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  const mat = new THREE.LineBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.4,
    depthWrite: false,
  });
  const lines = new THREE.LineSegments(geo, mat);
  lines.renderOrder = 5;
  return lines;
}

/** 太い線分の集まり（予約した経路。歩きは実線、フライトは点線） */
export class FatSegments {
  readonly object: LineSegments2;
  private readonly material: LineMaterial;

  constructor(color: number, widthPx: number, dashed = false) {
    this.material = new LineMaterial({
      color,
      linewidth: widthPx,
      depthTest: false,
      transparent: true,
      dashed,
      dashSize: 700,
      gapSize: 450,
    });
    this.object = new LineSegments2(new LineSegmentsGeometry(), this.material);
    this.object.renderOrder = 15;
    this.object.frustumCulled = false;
    this.object.visible = false;
  }

  setResolution(width: number, height: number): void {
    this.material.resolution.set(width, height);
  }

  setColor(color: number): void {
    this.material.color.setHex(color);
  }

  /** 地形より奥なら隠すか（追従カメラでは隠す。俯瞰の地図では常に見せる） */
  setDepthTest(on: boolean): void {
    if (this.material.depthTest === on) return;
    this.material.depthTest = on;
    this.material.needsUpdate = true;
  }

  /** 折れ線（x, y, z の並び）の集まりを描く。空なら非表示 */
  setPolylines(polylines: number[][]): void {
    const seg: number[] = [];
    for (const p of polylines) {
      for (let i = 0; i + 5 < p.length; i += 3) {
        seg.push(
          p[i] as number,
          p[i + 1] as number,
          p[i + 2] as number,
          p[i + 3] as number,
          p[i + 4] as number,
          p[i + 5] as number,
        );
      }
    }
    if (seg.length === 0) {
      this.object.visible = false;
      return;
    }
    this.object.geometry.dispose();
    const geo = new LineSegmentsGeometry();
    geo.setPositions(seg);
    this.object.geometry = geo;
    this.object.computeLineDistances();
    this.object.visible = true;
  }
}

/**
 * 歩いている経路の、今の位置から区間の終わりまでの折れ線（予約のルートは区間の終わりから先しか描かないので、その手前を補う）。
 * doneM：区間の始まりから歩いた距離、start：今の位置
 */
export function remainingArcPolyline(
  edge: EdgeData,
  forward: boolean,
  doneM: number,
  start: { x: number; y: number; z: number },
): number[] {
  const p = edge.polyline;
  const n = p.length / 3;
  const idx = (k: number): number => (forward ? k : n - 1 - k);
  let total = 0;
  for (let k = 1; k < n; k++) {
    const a = idx(k - 1) * 3;
    const b = idx(k) * 3;
    total += Math.hypot((p[b] as number) - (p[a] as number), (p[b + 2] as number) - (p[a + 2] as number));
  }
  const frac = edge.lengthM > 0 ? doneM / edge.lengthM : 0;
  const pts = [start.x, start.y, start.z];
  let cum = 0;
  for (let k = 1; k < n; k++) {
    const a = idx(k - 1) * 3;
    const b = idx(k) * 3;
    cum += Math.hypot((p[b] as number) - (p[a] as number), (p[b + 2] as number) - (p[a + 2] as number));
    if (total > 0 && cum / total > frac) pts.push(p[b] as number, p[b + 1] as number, p[b + 2] as number);
  }
  return pts;
}

/** 経路の弧の列を、ひと続きの折れ線にする（高さは経路の値のまま。地面に沿わせるのは drapePolyline） */
export function arcsToPolyline(edges: EdgeData[], arcs: Arc[]): number[] {
  const pts: number[] = [];
  for (const arc of arcs) {
    const p = (edges[arc.edgeId] as EdgeData).polyline;
    const n = p.length / 3;
    for (let k = 0; k < n; k++) {
      const i = arc.forward ? k : n - 1 - k;
      if (pts.length > 0 && k === 0) continue;
      pts.push(p[i * 3] as number, p[i * 3 + 1] as number, p[i * 3 + 2] as number);
    }
  }
  return pts;
}
